import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { AttentionIcon, LinkButton, Prompt, SnackbarStatus, Tooltip, useSnackbar } from '@weareopenr/component-library';
import classNames from 'classnames/bind';
import { useCancelCheck } from 'src/hooks/useCancelCheck';
import styles from './InlineNameForm.module.scss';

const cx = classNames.bind(styles);

interface IInlineNameFormValues {
  name: string;
}

export const InlineNameForm = ({
  onSubmit,
  onClose,
  existingNames,
  defaultValue = '',
}: {
  onSubmit: (name: string) => Promise<void>;
  onClose: () => void;
  existingNames: string[];
  defaultValue?: string;
}) => {
  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting, dirtyFields },
    setFocus,
  } = useForm<IInlineNameFormValues>({
    defaultValues: { name: defaultValue },
  });
  const { showSnackbar } = useSnackbar();
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    formRef.current?.scrollIntoView({ behavior: 'smooth' });
    setFocus('name');
  }, []);

  const { isCancelCheck, message, handleStay, handleLeave } = useCancelCheck({
    totalChanges: Object.keys(dirtyFields).length,
  });

  const submit = async (data: IInlineNameFormValues) => {
    try {
      await onSubmit(data.name);
      onClose();
    } catch (error_) {
      console.error('Failed to save:', error_);
      showSnackbar({ status: SnackbarStatus.ERROR, message: 'Unable to save. Please try again.' });
    }
  };

  return (
    <>
      <Prompt
        closePrompt={handleStay}
        open={isCancelCheck}
        buttonHandler={handleLeave}
        buttonText="Leave page"
        backButton
        backButtonText="Review changes"
        promptHeading="Unsaved changes"
        size="small"
      >
        {message}
      </Prompt>
      <form className={cx('form')} onSubmit={handleSubmit(submit)} ref={formRef}>
        <input
          className={cx('input', { 'input-error': errors.name })}
          {...register('name', {
            required: true,
            validate: {
              isNameUnique: (v) => {
                const exists = existingNames.find((name) => name.toLocaleLowerCase() === v.trim().toLocaleLowerCase());
                return exists ? 'This name already exists. Please enter a unique name' : true;
              },
            },
          })}
        />
        <LinkButton className={cx('save')} type="submit" disabled={isSubmitting}>
          Save
        </LinkButton>
        <LinkButton className={cx('cancel')} type="button" onClick={onClose}>
          Cancel
        </LinkButton>
        {errors.name && (
          <div className={cx('error-tooltip')}>
            <Tooltip text={errors.name.message || 'This field is required'}>
              <AttentionIcon className={styles['error-icon']} />
            </Tooltip>
          </div>
        )}
      </form>
    </>
  );
};
